import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "@/components/ui/use-toast";
import { usePreviewPerformance } from "@/dashboard/store/store";
import usePostData from "@/hooks/usePostData";
import i18n from "@/i18n";
import { cn } from "@/libs/utils";
import { Indicator } from "@/types";
import { ChevronDown, ChevronLeft, ChevronRight } from "lucide-react";
import { FC, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import ChartGlobal from "./ChartGlobal";

const getContextualCumulativeValue = (
  indicator: Indicator,
  context?: "program" | "programme" | "dashboard" | null
) => {
  if (context === "dashboard") {
    return indicator.cumulativeDashboard ?? indicator.cumulative ?? false;
  }

  if (context === "programme") {
    return indicator.cumulativeProgram ?? indicator.cumulative ?? false;
  }

  return indicator.cumulativeReporting ?? indicator.cumulative ?? false;
};

const ChartItemGlobal: FC<{
  dataReporting: any;
  minDate: any;
  maxDate: any;
  indicator: Indicator;
  className?: string;
  currentYear: number;
  width: string;
  maxTco2Year: number;
  context?: "program" | "programme" | "dashboard" | null;
}> = ({
  dataReporting,
  minDate,
  maxDate,
  indicator,
  className,
  width = "basis-1/2",
  maxTco2Year,
  context = null,
}) => {
    const size = width;
    const [isCollapsed, setIsCollapsed] = useState(true);
    const { previews, setPreviews } = usePreviewPerformance();
    const { postData } = usePostData<any>();
    const [dateArray, setDateArray] = useState<number[]>([]);
    const [currentYear, setCurrentYear] = useState(
      maxDate > new Date().getFullYear() ? new Date().getFullYear() : maxDate
    );
    const progIdParam = useParams().id;
    const progId = progIdParam ? parseInt(progIdParam, 10) : undefined;
    const { t } = useTranslation("dashboard", {
      keyPrefix: "program",
    });
    const [currentCumulative, setCurrentCumulative] = useState<boolean>(
      getContextualCumulativeValue(indicator, context)
    );

    useEffect(() => {
      setCurrentCumulative(getContextualCumulativeValue(indicator, context));
    }, [
      context,
      indicator.cumulative,
      indicator.cumulativeReporting,
      indicator.cumulativeDashboard,
      indicator.cumulativeProgram,
      indicator.id,
    ]);

    useEffect(() => {
      const DateArrayTemp: number[] = [];
      for (let i = minDate; i <= maxDate; i++) {
        DateArrayTemp.push(i);
      }
      setDateArray(DateArrayTemp);
    }, [minDate, maxDate]);
    useEffect(() => {
      if (/^tco2_sequestrees/.test(indicator.code)) {
        setPreviews({ name: indicator.name, value: "3" });
      } else {
        setPreviews({ name: indicator.name, value: "3" });
      }
    }, []);

    const handleYearChange = (action: "prev" | "next") => {
      const year = currentYear;
      setCurrentYear(action === "prev" ? year - 1 : year + 1);
    };

    const hasBlocking =
      indicator.prog_id === null &&
      indicator.programme === false &&
      indicator.programReporting === null;

    const isVisible =
      !hasBlocking &&
      (context === null ||
        (context === "dashboard" &&
          indicator.dashboard === true &&
          indicator.prog_id === null) ||
        (context === "programme" &&
          (((indicator.prog_id === null || indicator.prog_id === progId) &&
            indicator.programReporting !== null) ||
            ((indicator.prog_id === null && indicator.programme === true) ||
              (indicator.prog_id === progId &&
                indicator.programme === true)))));


    if (!isVisible) return null;

    const handleCHangeCumul = async (value: string) => {
      if (context !== "dashboard" && context !== "programme") {
        return;
      }

      const nextValue = value === "1";
      const previousValue = currentCumulative;
      setCurrentCumulative(nextValue);

      const payload = {
        currentItem: {
          id: indicator.id,
          prog_id: context === "programme" ? progId : null,
        },
        ...(context === "programme"
          ? { cumulativeProgram: nextValue }
          : { cumulativeDashboard: nextValue }),
        cumulativeReporting: nextValue,
      };

      const response = await postData({
        url: `${import.meta.env.VITE_API_ENDPOINT}/parameter/save/cumul`,
        data: payload,
      });

      if (!response) {
        setCurrentCumulative(previousValue);
        toast({
          className: "text-red-500",
          title: t("Une erreur est survenue lors de la sauvegarde."),
        });
        return;
      }

      toast({
        title: t("Paramètre sauvegardé"),
        description: t("Le mode de cumul a bien été mis à jour."),
      });
    };

    return (
      <div className={cn(`mb-4 ${size}`, className)}>
        <div className="group relative rounded-[20px] bg-card p-4 px-0 items-center">
          <h3
            className={`${isCollapsed ? "mb-4" : ""
              } ml-8 font-semibold text-muted-foreground flex flex-wrap items-center gap-2 cursor-pointer`}
            onClick={() => setIsCollapsed(!isCollapsed)}
          >
            <ChevronDown size={17} />
            {indicator.indicatorLangs?.find(
              (ptl) => ptl.lang.code === i18n.language
            )
              ? indicator.indicatorLangs?.find(
                (ptl) => ptl.lang.code === i18n.language
              )?.name
              : indicator.name}{" "}
            <div className="flex items-center">

              <Select
                value={currentCumulative ? "1" : "0"}
                onValueChange={(e) => {
                  handleCHangeCumul(e)
                }}
              >
                <SelectTrigger className="text-foreground/90 outline-none focus:ring-0 focus:ring-transparent w-28 bg-card border-none">
                  <SelectValue
                    placeholder={
                      currentCumulative ? "Cumulatif" : "Non Cumulatif"
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    <SelectItem value="1">Cumulatif</SelectItem>
                  </SelectGroup>
                  <SelectGroup>
                    <SelectItem value="0">Non Cumulatif</SelectItem>
                  </SelectGroup>
                </SelectContent>
              </Select>
            </div>
          </h3>
          <div className=" flex items-center">
            <div className=" absolute right-10 top-2 flex items-center">
              Affichage :{" "}
              <Select
                onValueChange={(e) => {
                  setPreviews({ name: indicator.name, value: e });
                }}
              >
                <SelectTrigger className="text-foreground/90 outline-none focus:ring-0 focus:ring-transparent w-28 bg-card border-none">
                  <SelectValue
                    placeholder={
                      previews[indicator.name] == "1"
                        ? "Mois"
                        : previews[indicator.name] == "2"
                          ? "Trimestre"
                          : "Année"
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    <SelectItem value="1">Mois</SelectItem>
                  </SelectGroup>
                  <SelectGroup>
                    <SelectItem value="2">Trimestre</SelectItem>
                    <SelectItem value="3">Année</SelectItem>
                  </SelectGroup>
                </SelectContent>
              </Select>
            </div>

          </div>
          {isCollapsed && (
            <>
              <ChartGlobal
                dataReporting={dataReporting}
                minDate={minDate}
                maxDate={maxDate}
                indicator={indicator}
                currentYear={currentYear}
                maxTco2Year={maxTco2Year}
                option={previews[indicator.name]}
                context={context}
                isCumulative={currentCumulative}
              />
              <Indice />
              <div className="flex gap-2 items-center justify-end mr-3">
                <div className="flex gap-1">
                  <Button
                    onClick={() => handleYearChange("prev")}
                    className="flex size-8 items-center justify-center rounded-full bg-card p-0 transition-all hover:bg-gray-200"
                    disabled={currentYear - 1 < minDate}
                  >
                    <ChevronLeft className="text-foreground/70" />
                  </Button>
                  <Button
                    onClick={() => handleYearChange("next")}
                    className="flex size-8 items-center justify-center rounded-full bg-card p-0 transition-all hover:bg-gray-200"
                    disabled={currentYear + 1 > maxDate}
                  >
                    <ChevronRight className="text-foreground/70" />
                  </Button>
                </div>
                <Select
                  value={currentYear.toString()}
                  onValueChange={(e) => {
                    setCurrentYear(Number(e));
                  }}
                >
                  <SelectTrigger className="w-16 border-0 px-0 text-lg text-foreground/90 outline-none focus:ring-0 focus:ring-transparent bg-card">
                    <SelectValue placeholder={currentYear?.toString()} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      {dateArray.map((i, k) => (
                        <SelectItem value={i.toString()} key={k}>
                          {i}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  </SelectContent>
                </Select>
              </div>
            </>
          )}
        </div>
      </div>
    );
  };

const Indice = () => {
  const { t } = useTranslation("dashboard", {
    keyPrefix: "program",
  });
  return (
    <div className="ml-10 mt-5 flex items-center gap-3">
      <div className="flex items-center gap-1">
        <div className={`size-2 rounded-full bg-verdeoblue`}></div>
        {t("Prévu")}
      </div>
      <div className="flex items-center gap-1">
        <div className={`size-2 rounded-full bg-verdeogreen`}></div>
        {t("Réalisé")}
      </div>
      <div className="flex items-center gap-1">
        <div className={`size-2 rounded-full bg-verdeoorange`}></div>
        {t("Verifié / justifié / certifié")}
      </div>
    </div>
  );
};

export default ChartItemGlobal;
